interface Item {
  id: number | null;
}

export function mergeUploaded<T extends Item, Key extends keyof T>(
  uploaded: T[],
  current: T[],
  prop: Key,
) {
  const replaced = new Map<number, number>();
  const matched = new Set<number>();
  const currentItemIdMap = new Map(
    current
      .map((item, index) => [item.id, index] as const)
      .filter(([id]) => id !== -1),
  );

  // Find all indexes of uploaded items where id matches with current item's id
  uploaded.forEach((item, index) => {
    if (item.id) {
      const replacedIndex = currentItemIdMap.get(item.id);
      if (replacedIndex !== undefined) {
        replaced.set(replacedIndex, index);
        matched.add(index);
      }
    }
  });

  // Find all indexes of uploaded items that are not matched yet
  // where property matches with current set's property
  uploaded.forEach((item, index) => {
    if (!matched.has(index)) {
      const replacedIndex = current.findIndex(
        (cr, i) => cr[prop] === item[prop] && !replaced.has(i),
      );
      if (replacedIndex !== -1) {
        replaced.set(replacedIndex, index);
        matched.add(index);
      }
    }
  });

  // Replace current items with matches
  replaced.forEach((val, key) => {
    current.splice(key, 1, uploaded[val]);
  });

  // Return matched current items and uploaded items without matches
  return current.concat(uploaded.filter((_, index) => !matched.has(index)));
}

export function mergeRequiredKeys<
  T extends Item & { key: string; sequenceNumber: number },
>(uploaded: T[], current: T[]): T[] {
  let sequenceNumber = Math.max(0, ...current.map((key) => key.sequenceNumber));

  return mergeUploaded(uploaded, [...current], 'key').map((key, index) => ({
    ...key,
    sequenceNumber:
      index < current.length ? current[index].sequenceNumber : ++sequenceNumber,
  }));
}
